/**
 * QUIPWA — which QUIPWA tabs are open on this device (BroadcastChannel)
 *
 * Every tab says `hello` when it loads and `active` when it is shown or
 * focused, and answers a `ping` with `here`. The exam (lib/exam/
 * integrity-monitor.ts) listens: another QUIPWA tab opened or used during a
 * session is an integrity incident (consulting the note in parallel), and
 * before starting it counts the tabs already open.
 *
 * Same origin only, never leaves the device, and nothing but a random id per
 * tab goes on the channel. Tiny on purpose: it runs on every page.
 */

export const TAB_CHANNEL = 'quipwa-tabs'

export type TabMessage =
  | { type: 'hello' | 'active' | 'here'; id: string }
  | { type: 'ping'; id: string }

/** This tab's id: messages carrying it are our own and are ignored. */
export const TAB_ID: string = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random())

function open(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(TAB_CHANNEL)
}

let started = false

/** Called once from main.tsx. */
export function startTabPresence(): void {
  if (started) return
  const channel = open()
  if (!channel) return
  started = true
  const say = (type: 'hello' | 'active' | 'here') => channel.postMessage({ type, id: TAB_ID } satisfies TabMessage)
  channel.onmessage = (event: MessageEvent<TabMessage>) => {
    if (event.data?.type === 'ping' && event.data.id !== TAB_ID) say('here')
  }
  say('hello')
  const active = () => {
    if (document.visibilityState === 'visible') say('active')
  }
  document.addEventListener('visibilitychange', active)
  window.addEventListener('focus', active)
}

/** How many other QUIPWA tabs answer within `waitMs`. */
export function countOtherTabs(waitMs = 250): Promise<number> {
  const channel = open()
  if (!channel) return Promise.resolve(0)
  const seen = new Set<string>()
  channel.onmessage = (event: MessageEvent<TabMessage>) => {
    const d = event.data
    if ((d?.type === 'here' || d?.type === 'hello') && d.id !== TAB_ID) seen.add(d.id)
  }
  channel.postMessage({ type: 'ping', id: TAB_ID } satisfies TabMessage)
  return new Promise((resolve) => {
    setTimeout(() => {
      channel.close()
      resolve(seen.size)
    }, waitMs)
  })
}

/** Calls `listener` when another QUIPWA tab opens or is used. Returns the way to stop. */
export function onOtherTab(listener: () => void): () => void {
  const channel = open()
  if (!channel) return () => undefined
  channel.onmessage = (event: MessageEvent<TabMessage>) => {
    const d = event.data
    if ((d?.type === 'hello' || d?.type === 'active') && d.id !== TAB_ID) listener()
  }
  return () => channel.close()
}
